import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import {Mesh, Object3D, Vector3, Quaternion} from 'three'
import type { ThreeEvent } from '@react-three/fiber'
import type { Dispatch, SetStateAction } from 'react'
import { markerURL, isMarkerVisibleOnCam } from '../utils'
import type { ActiveAnimations } from './AnimationMixer'

interface InteractablesProps {
  interactables: Object3D[]
  activeCam: number
  onHoverChange?: (name: string | null) => void
  onOpenModal?: (url: string) => void
  setActiveAnimations?: Dispatch<SetStateAction<ActiveAnimations>>
  isCurrentlyPlayingAnimations?: boolean
}

function Interactables({ interactables, activeCam, onHoverChange, onOpenModal, setActiveAnimations, isCurrentlyPlayingAnimations }: InteractablesProps) {
  const trigger = useCallback((mesh: Mesh) => {
    const entry = Object.entries(markerURL).find(([key]) => mesh.name.includes(key))
    if (!entry) return
    const value = entry[1]
    if (typeof value === 'string') {
      onOpenModal?.(value)
      return
    }
    setActiveAnimations?.((prev) => {
      const sameLength = prev.animationsList.length === value.anim.length
      const isSame = sameLength && prev.animationsList.every((name, i) => name === value.anim[i])
      if (isSame && isCurrentlyPlayingAnimations) return prev
      return { animationsList: value.anim, callback: value.callback ?? (() => {
          onOpenModal?.(value.url)
        }) }
    })
  }, [onOpenModal, setActiveAnimations, isCurrentlyPlayingAnimations])

  // A press only counts as a click if the release lands on the same object's
  // silhouette. We arm on pointer-down and fire on pointer-up; primitives of a
  // multi-mesh object (e.g. credits_0..credits_4) share one logical id so the
  // press and release can fall on different primitives of the same object.
  const armedRef = useRef<string | null>(null)
  const idOf = (mesh: Mesh) => mesh.name.replace(/_\d+$/, '')

  // Any press that starts off every interactable clears the arm, so releasing
  // onto one (e.g. at the end of a camera drag) can never trigger it.
  useEffect(() => {
    const clear = () => { armedRef.current = null }
    window.addEventListener('pointerdown', clear, true)
    return () => window.removeEventListener('pointerdown', clear, true)
  }, [])

  // A camera change (back button, marker, browser back) voids any pending press:
  // the release that follows must not fire the interactable that was armed in the
  // previous view — and the new view may even place a same-id proxy under the cursor.
  // Also drop the hover outline: the previously hovered proxy is gone/moved, but
  // r3f won't fire pointer-out for it, so it would otherwise stay lit.
  useEffect(() => {
    armedRef.current = null
    document.body.style.cursor = 'auto'
    onHoverChange?.(null)
  }, [activeCam, onHoverChange])

  // r3f raycasts the scene even under DOM overlays, so a proxy behind a camera
  // marker / the go-back button / the modal still receives pointer events when
  // you're really pointing at that overlay. A genuine interactable interaction
  // points straight at the canvas (nothing on top), so the native event target
  // is the canvas itself; any overlay makes it some other element. Gate on that.
  const isOverCanvas = (e: ThreeEvent<PointerEvent>) => e.nativeEvent.target instanceof HTMLCanvasElement

  const onPointerDown = useCallback((mesh: Mesh) => (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    if (!isOverCanvas(e)) return
    armedRef.current = idOf(mesh)
  }, [])

  // Fire on click (the native DOM click on the canvas), not pointer-up: a click
  // only exists when down+up land on the same element, and crucially r3f never
  // produces one for sibling DOM overlays like the go-back button (their click
  // can't bubble into the canvas container), so those can't leak a trigger. The
  // armed check additionally requires the press to have started on this object.
  const onClick = useCallback((mesh: Mesh) => (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation()
    if (!(e.nativeEvent.target instanceof HTMLCanvasElement)) return
    if (armedRef.current !== idOf(mesh)) return
    armedRef.current = null
    trigger(mesh)
  }, [trigger])

  // Proxies are placed by the mesh's *world* transform, not its local one.
  // Single-primitive GLTF nodes load as a Mesh carrying the node transform, but
  // multi-primitive nodes load as a Group whose child meshes sit at local origin
  // — using the local transform would drop those proxies at (0,0,0) and make the
  // object impossible to hover/click even though its outline renders correctly.
  const clickable = useMemo(() => {
    const activeCamKey = `cam${activeCam + 1}`
    return interactables
      .filter((obj) => isMarkerVisibleOnCam(obj.name, activeCamKey))
      .map((obj) => {
        const mesh = obj as Mesh
        mesh.updateWorldMatrix(true, false)
        const position = new Vector3()
        const quaternion = new Quaternion()
        const scale = new Vector3()
        mesh.matrixWorld.decompose(position, quaternion, scale)
        return { mesh, position, quaternion, scale }
      })
  }, [interactables, activeCam])

  // Used for both enter and move: while the pointer rides over a marker the proxy
  // is still intersected (so r3f never fires pointer-out), so we must actively
  // drop the hover here instead of relying on onPointerOut.
  const updateHover = useCallback((mesh: Mesh, e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    if (!isOverCanvas(e)) {
      document.body.style.cursor = 'auto'
      onHoverChange?.(null)
      return
    }
    document.body.style.cursor = 'pointer'
    onHoverChange?.(mesh.name.replace(/_\d+$/, ''))
  }, [onHoverChange])

  const onPointerOver = useCallback((mesh: Mesh) => (e: ThreeEvent<PointerEvent>) => updateHover(mesh, e), [updateHover])
  const onPointerMove = useCallback((mesh: Mesh) => (e: ThreeEvent<PointerEvent>) => updateHover(mesh, e), [updateHover])

  const onPointerOut = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation()
    document.body.style.cursor = 'auto'
    onHoverChange?.(null)
  }, [onHoverChange])

  return (
    <>
      {clickable.map(({ mesh, position, quaternion, scale }) => (
        <mesh
          key={mesh.uuid}
          geometry={mesh.geometry}
          position={position}
          quaternion={quaternion}
          scale={scale}
          visible={false}
          onPointerDown={onPointerDown(mesh)}
          onClick={onClick(mesh)}
          onPointerOver={onPointerOver(mesh)}
          onPointerMove={onPointerMove(mesh)}
          onPointerOut={onPointerOut}
        />
      ))}
    </>
  )
}

export default memo(Interactables)
